/**
 * Languages Management Component
 *
 * Displays and manages notification languages (global).
 * Can be used standalone or embedded in NotificationHub.
 */

import React, { useEffect, useRef, useState } from "react";
import { AppCard } from "../../components/ui";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { InputSwitch } from "primereact/inputswitch";
import { confirmDialog } from "primereact/confirmdialog";
import { Pencil, Trash2, Ban, CheckCircle } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import Loader from "../../components/Loader";
import { languagesApiService } from "../../services/api/notifications";
import { LanguageDto, CreateLanguageRequest, UpdateLanguageRequest } from "../../models/notifications";
import { storageService } from "../../services/StorageService";

const Languages: React.FC = () => {
    const toast = useRef<Toast>(null);
    const merchantId = Number(storageService.getSelectedMerchantId());

    const [isLoading, setIsLoading] = useState(true);
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [showInactive, setShowInactive] = useState(false);

    // Editor state
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState<LanguageDto | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Form state
    const [formIsoCode, setFormIsoCode] = useState("");
    const [formName, setFormName] = useState("");
    const [formIsActive, setFormIsActive] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadLanguages();
    }, [showInactive]);

    const loadLanguages = async () => {
        setIsLoading(true);
        const result = await languagesApiService.getAll(merchantId, showInactive);
        if (result.success) {
            setLanguages(result.data);
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to load languages",
                life: 4000,
            });
        }
        setIsLoading(false);
    };

    const openCreate = () => {
        setEditingLanguage(null);
        setFormIsoCode("");
        setFormName("");
        setFormIsActive(true);
        setErrors({});
        setEditorVisible(true);
    };

    const openEdit = (language: LanguageDto) => {
        setEditingLanguage(language);
        setFormIsoCode(language.isoCode);
        setFormName(language.name);
        setFormIsActive(language.isActive);
        setErrors({});
        setEditorVisible(true);
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formIsoCode.trim()) {
            newErrors.isoCode = "ISO code is required";
        } else if (!/^[a-zA-Z]{2}(-[a-zA-Z]{2})?$/i.test(formIsoCode)) {
            newErrors.isoCode = "ISO code must be like 'en' or 'en-US'";
        }

        if (!formName.trim()) {
            newErrors.name = "Name is required";
        } else if (formName.length > 100) {
            newErrors.name = "Name must be 100 characters or less";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setIsSaving(true);

        try {
            if (editingLanguage) {
                const request: UpdateLanguageRequest = {
                    name: formName,
                    isActive: formIsActive,
                };
                const result = await languagesApiService.update(editingLanguage.languageId, request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Updated",
                        detail: `Language "${formName}" has been updated`,
                        life: 3000,
                    });
                    setEditorVisible(false);
                    loadLanguages();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to update language",
                        life: 4000,
                    });
                }
            } else {
                // Normalize ISO code: lowercase language, uppercase region (e.g., "en-US")
                const normalizedIsoCode = formIsoCode.includes('-')
                    ? formIsoCode.split('-')[0].toLowerCase() + '-' + formIsoCode.split('-')[1].toUpperCase()
                    : formIsoCode.toLowerCase();
                    
                const request: CreateLanguageRequest = {
                    merchantId,
                    isoCode: normalizedIsoCode,
                    name: formName,
                    isActive: formIsActive,
                };
                const result = await languagesApiService.create(request);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Created",
                        detail: `Language "${formName}" has been created`,
                        life: 3000,
                    });
                    setEditorVisible(false);
                    loadLanguages();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to create language",
                        life: 4000,
                    });
                }
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (language: LanguageDto) => {
        confirmDialog({
            message: `Are you sure you want to delete "${language.name}"? This cannot be undone.`,
            header: "Delete Language",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                const result = await languagesApiService.delete(language.languageId);
                if (result.success) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: `Language "${language.name}" has been deleted`,
                        life: 3000,
                    });
                    loadLanguages();
                } else {
                    toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: result.message || "Failed to delete language",
                        life: 4000,
                    });
                }
            },
        });
    };

    const handleToggleActive = async (language: LanguageDto) => {
        const result = language.isActive
            ? await languagesApiService.deactivate(language.languageId)
            : await languagesApiService.activate(language.languageId);

        if (result.success) {
            toast.current?.show({
                severity: "success",
                summary: "Updated",
                detail: `Language "${language.name}" has been ${language.isActive ? "deactivated" : "activated"}`,
                life: 3000,
            });
            loadLanguages();
        } else {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: result.message || "Failed to update language status",
                life: 4000,
            });
        }
    };

    // Column templates
    const isoCodeTemplate = (row: LanguageDto) => (
        <Tag value={row.isoCode} className="font-mono" />
    );

    const statusTemplate = (row: LanguageDto) => (
        <Tag
            value={row.isActive ? "Active" : "Inactive"}
            severity={row.isActive ? "success" : "warning"}
        />
    );

    const actionsTemplate = (row: LanguageDto) => (
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={() => openEdit(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                title="Edit"
            >
                <Pencil size={15} />
            </button>
            <button
                type="button"
                onClick={() => handleToggleActive(row)}
                className={`p-1.5 rounded-md transition-colors ${row.isActive
                    ? "text-neutral-400 hover:text-warning-600 hover:bg-warning-50 dark:hover:text-warning-400 dark:hover:bg-warning-900/20"
                    : "text-neutral-400 hover:text-success-600 hover:bg-success-50 dark:hover:text-success-400 dark:hover:bg-success-900/20"
                }`}
                title={row.isActive ? "Deactivate" : "Activate"}
            >
                {row.isActive ? <Ban size={15} /> : <CheckCircle size={15} />}
            </button>
            <button
                type="button"
                onClick={() => handleDelete(row)}
                className="p-1.5 rounded-md text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                title="Delete"
            >
                <Trash2 size={15} />
            </button>
        </div>
    );

    const header = (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                    <InputSwitch
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.value ?? false)}
                    />
                    <span className="text-sm text-gray-600">Show inactive</span>
                </div>
            </div>
            <Button
                size="small"
                label="Add Language"
                icon="pi pi-plus"
                onClick={openCreate}
            />
        </div>
    );

    const editorFooter = (
        <div className="flex justify-end gap-2">
            <Button
                size="small"
                label="Cancel"
                icon="pi pi-times"
                outlined
                onClick={() => setEditorVisible(false)}
                disabled={isSaving}
            />
            <Button
                size="small"
                label={editingLanguage ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSaving}
            />
        </div>
    );

    if (isLoading && languages.length === 0) {
        return <Loader />;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-center" />

            <AppCard>
                <AppCard.Header>
                    <div className="flex flex-col gap-1">
                        <span>Languages</span>
                        <small className="text-neutral-500 font-normal">
                            Manage available languages for notification template localizations.
                        </small>
                    </div>
                </AppCard.Header>
                <AppCard.Body>
                    <DataTable
                        value={languages}
                        loading={isLoading}
                        header={header}
                        emptyMessage="No languages found"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        stripedRows
                        pt={{
                            header: { className: "border-gray-200 rounded-t-lg" },
                            wrapper: { className: "border-gray-200 border-x border-b" },
                            bodyRow: { className: "even:!bg-blue-50/50" },
                        }}
                    >
                        <Column
                            header="ISO Code"
                            body={isoCodeTemplate}
                            style={{ width: "120px" }}
                        />
                        <Column
                            field="name"
                            header="Name"
                            sortable
                        />
                        <Column
                            header="Status"
                            body={statusTemplate}
                            style={{ width: "100px" }}
                        />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            style={{ width: "150px" }}
                        />
                    </DataTable>
                </AppCard.Body>
            </AppCard>

            {/* Editor Dialog */}
            <Dialog
                header={editingLanguage ? `Edit Language: ${editingLanguage.name}` : "Add Language"}
                visible={editorVisible}
                style={{ width: "400px" }}
                modal
                onHide={() => setEditorVisible(false)}
                footer={editorFooter}
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            ISO Code <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={formIsoCode}
                            onChange={(e) => setFormIsoCode(e.target.value)}
                            placeholder="e.g., en, bg, en-US"
                            className={`w-full font-mono ${errors.isoCode ? "p-invalid" : ""}`}
                            disabled={!!editingLanguage}
                            maxLength={10}
                        />
                        {errors.isoCode && (
                            <small className="p-error block mt-1">{errors.isoCode}</small>
                        )}
                        {!editingLanguage && (
                            <small className="text-gray-500 block mt-1">
                                Use ISO 639-1 codes (e.g., en, bg, de) or with region (e.g., en-US)
                            </small>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">
                            Display Name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            placeholder="e.g., English, Bulgarian"
                            className={`w-full ${errors.name ? "p-invalid" : ""}`}
                            maxLength={100}
                        />
                        {errors.name && (
                            <small className="p-error block mt-1">{errors.name}</small>
                        )}
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <Checkbox
                            inputId="langIsActive"
                            checked={formIsActive}
                            onChange={(e) => setFormIsActive(e.checked ?? true)}
                        />
                        <label htmlFor="langIsActive" className="cursor-pointer">
                            Active (available for template localizations)
                        </label>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default Languages;
